/**
 * SEO HEADING STRUCTURE - Homepage (Index)
 * ========================================
 * TITLE: CSW - Study Stream | Study With Me | Body Doubling | Study Together
 * H1: "Alone. But never lonely." (Hero)
 * H2: "Real-Time Collaborative Workspaces" (LiveWorkspaces)
 * H2: "Built for Deep Focus" (DeepFocus)
 * H2: "Built for Students" (ForStudents)
 * H2: "Built for Institutions" (ForInstitutions)
 * H2: "The Behavioral Layer of Collaborative Work and Learning" (BehavioralLayer)
 */

import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { LiveWorkspaces } from "@/components/home/LiveWorkspaces";
import { DeepFocus } from "@/components/home/DeepFocus";
import { ForStudents, ForInstitutions } from "@/components/home/AudienceSections";
import { BehavioralLayer } from "@/components/home/BehavioralLayer";
import { CTA } from "@/components/CTA";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";


const homepageSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": "https://csw.live/#webpage",
      "url": "https://csw.live/",
      "name": "CSW - Study Stream | Study With Me | Body Doubling | Study Together Online",
      "description": "Free virtual study rooms powered by body doubling. Join study streams, study with me sessions, and study together with a global community. Stay focused with visual accountability.",
      "inLanguage": "en",
      "dateModified": "2026-03-22",
      "isPartOf": {
        "@type": "WebSite",
        "@id": "https://csw.live/#website",
        "url": "https://csw.live/",
        "name": "CSW",
        "publisher": { "@type": "Organization", "name": "CSW", "url": "https://csw.live" }
      },
      "about": {
        "@type": "SoftwareApplication",
        "name": "CSW",
        "applicationCategory": "ProductivityApplication",
        "operatingSystem": "Web Browser",
        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5", "ratingCount": "3000", "bestRating": "5", "worstRating": "1" }
      },
      "keywords": [
        "study stream", "study with me", "body doubling", "study together",
        "virtual study room", "study with strangers", "online study room",
        "social accountability", "focus rooms", "free study app"
      ]
    },
    {
      "@type": "SoftwareApplication",
      "@id": "https://csw.live/#software",
      "name": "CSW - Chill Study Work",
      "applicationCategory": "ProductivityApplication",
      "operatingSystem": "Web Browser",
      "description": "Free body doubling platform with study streams, study with me sessions, and virtual study rooms. Study together with a global community.",
      "url": "https://csw.live",
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD", "availability": "https://schema.org/InStock" },
      "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5", "ratingCount": "3000", "bestRating": "5", "worstRating": "1" }
    },
    {
      "@type": "Organization",
      "@id": "https://csw.live/#organization",
      "name": "CSW",
      "alternateName": "Chill Study Work",
      "url": "https://csw.live",
      "logo": "https://csw.live/favicon.png",
      "sameAs": [
        "https://www.instagram.com/csw.live/",
        "https://www.youtube.com/@CSW.live.workspace"
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "info@csw.live",
        "contactType": "customer support",
        "availableLanguage": ["English", "French", "Japanese", "Korean", "Vietnamese", "Chinese"]
      }
    },
    {
      "@type": "BreadcrumbList",
      "@id": "https://csw.live/#breadcrumb",
      "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Home", "item": "https://csw.live/" }]
    },
    {
      "@type": "FAQPage",
      "@id": "https://csw.live/#faq",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is CSW?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "CSW is a free online study room and body doubling platform. Join live study streams and virtual rooms to study alongside others on camera — so you focus better when you're not alone."
          }
        },
        {
          "@type": "Question",
          "name": "Is CSW really free?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. CSW is free forever with no limits — no credit card, no trial. Join study streams, body doubling sessions, and virtual study rooms at no cost."
          }
        },
        {
          "@type": "Question",
          "name": "What is body doubling?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Body doubling is the practice of working alongside another person — physically or virtually — to stay focused and accountable. CSW makes body doubling accessible 24/7 through live study streams with people around the world."
          }
        },
        {
          "@type": "Question",
          "name": "How does a study stream work?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Join a live study stream, turn on your camera, set your goal, and study alongside other students in real time. Visual accountability helps you stay focused longer and finish more sessions."
          }
        },
        {
          "@type": "Question",
          "name": "Who is CSW for?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "CSW is built for students, professionals, researchers, and creators — including ADHD and neurodivergent users who benefit from visual structure and shared focus time. Institutions also use CSW to support student success."
          }
        }
      ]
    }
  ]
};

const Index = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="CSW - Study Stream | Study With Me | Body Doubling | Study Together"
        description="Free virtual study rooms powered by body doubling. Join study streams and stay focused with a global community."
        keywords="study stream, study with me, body doubling, study together, virtual study room, study with strangers, social accountability, focus rooms"
        canonicalUrl="/"
        isHomepage={true}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(homepageSchema)}
        </script>
      </Helmet>
      <Header />
      <main>
        <Hero />
        <LiveWorkspaces />
        <DeepFocus />
        <ForStudents />
        <ForInstitutions />
        <BehavioralLayer />
        <CTA />
      </main>

      <Footer />
    </div>
  );
};

export default Index;
